import { inject, Injectable, signal } from '@angular/core';
import { Observable, finalize, switchMap, tap } from 'rxjs';
import { EMAIL_VERIFICATION, PHONE_NUMBER_VERIFICATION } from '@app/core/core.constants';
import { AnalyticsEvents } from '@app/core/enums/analytics-events.enum';
import { ElementActions, ElementNames, ElementTypes } from '@app/core/enums/ui-interaction.enum';
import { ProcessNames, ProcessSteps } from '@app/core/enums/process.enum';
import { FeatureFlagStore } from '@app/core/feature-flags/feature-flag.store';
import { FeatureFlags } from '@app/core/feature-flags/feature-flag.types';
import { AnalyticsService } from '@app/core/services/analytics.service';
import { AppConfigService } from '@app/core/services/config.service';
import { RecaptchaService } from '@app/core/services/recaptcha.service';
import { PolicyService } from '@app/core/services/policy.service';
import { RegistrationDataService } from '@app/core/services/registration-data.service';
import { PolicyConditions } from '@app/core/types/policy.types';
import { VerificationChannel } from '@app/core/types/membership/membership.types';
import { CountryISO } from 'ngx-intl-tel-input-gg';
import { resolvePhoneCountry } from '@app/shared/utils/phone.util';
import { RegistrationService } from '../services/registration.service';
import {
  LinkEmailRequest,
  LinkPhoneContext,
  LinkPhoneRequest,
  RegisterAccountRequest,
  RegistrationDraft,
} from '../model/registration.types';

@Injectable({ providedIn: 'root' })
export class RegistrationSandbox {
  private readonly registrationService = inject(RegistrationService);
  private readonly policyService = inject(PolicyService);
  private readonly recaptchaService = inject(RecaptchaService);
  private readonly registrationDataService = inject(RegistrationDataService);
  private readonly configService = inject(AppConfigService);
  private readonly featureFlagStore = inject(FeatureFlagStore);
  private readonly analyticsService = inject(AnalyticsService);

  linkPhoneContext = signal<LinkPhoneContext | null>(null);
  draft = signal<RegistrationDraft | null>(null);
  termsAndConditions = signal<PolicyConditions | null>(null);
  privacyPolicy = signal<PolicyConditions | null>(null);

  submitting = signal(false);

  readonly prefill = this.registrationDataService.registrationData;

  get preferredCountries(): CountryISO[] {
    return this.configService.preferredCountries ?? [];
  }

  get defaultCountry(): CountryISO | undefined {
    return resolvePhoneCountry(
      this.configService.defaultCountry.alpha2Code,
      this.configService.defaultCountry.isDefaultCountryForPhoneNumber,
      this.preferredCountries
    );
  }

  readonly whatsAppVerificationEnabled = this.featureFlagStore.getFeatureFlag(
    FeatureFlags.WhatsAppPhoneVerification
  );

  readonly marketingConsentEnabled = this.featureFlagStore.getFeatureFlag(
    FeatureFlags.MarketingConsent
  );

  loadPolicies(): void {
    this.policyService
      .getTermsAndConditions()
      .subscribe((terms) => this.termsAndConditions.set(terms));
    this.policyService.getPrivacyPolicy().subscribe((policy) => this.privacyPolicy.set(policy));
  }

  setDraft(draft: RegistrationDraft): void {
    this.draft.set(draft);
  }

  resetFlow(): void {
    this.draft.set(null);
    this.linkPhoneContext.set(null);
  }

  setLinkPhoneContext(context: LinkPhoneContext): void {
    this.linkPhoneContext.set(context);
  }

  clearLinkPhoneContext(): void {
    this.linkPhoneContext.set(null);
  }

  checkPhoneExists(phoneNumber: string): Observable<boolean> {
    return this.registrationService.checkPhoneExists(phoneNumber);
  }

  sendVerificationCode(phoneNumber: string, channel: VerificationChannel): Observable<void> {
    return this.withCaptcha(PHONE_NUMBER_VERIFICATION).pipe(
      switchMap((token) =>
        this.registrationService.startPhoneVerification(phoneNumber, token, channel)
      )
    );
  }

  sendEmailVerificationCode(email: string): Observable<void> {
    return this.withCaptcha(EMAIL_VERIFICATION).pipe(
      switchMap((token) => this.registrationService.startEmailVerification(email, token))
    );
  }

  checkEmailVerificationCode(email: string, verificationCode: string): Observable<boolean> {
    return this.registrationService.checkEmailVerification(email, verificationCode);
  }

  checkVerificationCode(phoneNumber: string, verificationCode: string): Observable<boolean> {
    return this.registrationService.checkPhoneVerification(phoneNumber, verificationCode);
  }

  linkEmail(request: LinkEmailRequest): Observable<void> {
    this.submitting.set(true);

    return this.registrationService.linkEmail(request).pipe(
      tap(() => this.completeRegistration()),
      finalize(() => this.submitting.set(false))
    );
  }

  linkPhone(request: LinkPhoneRequest): Observable<void> {
    this.submitting.set(true);

    return this.registrationService.linkPhone(request).pipe(
      tap(() => this.completeRegistration()),
      finalize(() => this.submitting.set(false))
    );
  }

  register(user: RegisterAccountRequest['user']): Observable<void> {
    const request: RegisterAccountRequest = {
      user,
      consents: {
        termsConditionsId: this.termsAndConditions()?.id ?? '',
        // Absent when the brand doesn't ask, so the API can tell "declined" from "not asked".
        ...(this.marketingConsentEnabled() && { marketing: this.draft()?.marketingConsent ?? false }),
      },
    };

    this.submitting.set(true);

    return this.registrationService.register(request).pipe(
      tap(() => this.completeRegistration()),
      finalize(() => this.submitting.set(false))
    );
  }

  logRegistrationStart(): void {
    this.logRegistrationStep(ProcessSteps.RegistrationStart);
  }

  logResendCode(step: ProcessSteps): void {
    this.analyticsService.logEvent(AnalyticsEvents.UI_interaction, {
      type: ElementTypes.Button,
      name: ElementNames.ResendCode,
      action: ElementActions.Click,
      sub_name: step,
    });
  }

  logRegistrationStep(step: ProcessSteps): void {
    this.analyticsService.logEvent(AnalyticsEvents.Process, {
      name: ProcessNames.Registration,
      step,
    });
  }

  private withCaptcha(action: string): Observable<string> {
    return new Observable<string>((subscriber) => {
      this.recaptchaService.execute(action, (token: string) => {
        subscriber.next(token);
        subscriber.complete();
      });
    });
  }

  private completeRegistration(): void {
    this.clearCollectedState();
    this.analyticsService.logEvent(AnalyticsEvents.ProcessCompleted, {
      name: ProcessNames.Registration,
    });
  }

  private clearCollectedState(): void {
    this.registrationDataService.clearRegistrationData();
    this.resetFlow();
  }
}
